import React from 'react';
import type { MenuDataItem } from '@ant-design/pro-layout';
import { SpaceAuthority } from '@/interfaces/character';
import { UserInfo } from '@/interfaces/user';
import { uniqBy } from 'lodash';
import $i18n from './i18n';
import { isPublicDeploy } from './utils/config';

const menus = {
  overview: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.Overview', dm: '首页' }),
    path: '/overview',
    icon: <span className="utopia_iconfont">&#xe967;</span>,
    sort: 1,
  },
  miniapp: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.miniapp.management', dm: '应用管理' }),
    path: '/miniapp',
    icon: <span className="utopia_iconfont">&#xe9ab;</span>,
    sort: 2,
  },
  members: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.MemberList', dm: '成员列表' }),
    path: '/members',
    icon: <span className="utopia_iconfont">&#xe882;</span>,
    sort: 3,
  },
  approvals: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.TaskManagement', dm: '任务管理' }),
    path: '/approvals',
    icon: <span className="utopia_iconfont">&#xe966;</span>,
    sort: 4,
  },
  channelSetting: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.AppSettings', dm: '应用设置' }),
    path: '/channelSetting',
    icon: <span className="utopia_iconfont">&#xe82b;</span>,
    sort: 5,
  },
  channels: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.MyChannel', dm: '小程序商店' }),
    path: '/miniAppStore',
    icon: <span className="utopia_iconfont">&#xe830;</span>,
    sort: 6,
  },
  invitation: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.ThreePartyPartnerInvitation', dm: '三方伙伴邀请' }),
    path: '/invitation',
    icon: <span className="utopia_iconfont">&#xe880;</span>,
    sort: 7,
  },
  duiba: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.GameOperationPlatform', dm: '游戏运营平台' }),
    path: '/duiba',
    icon: <span className="utopia_iconfont">&#xe9ff;</span>,
    sort: 8,
  },
  miniappOperationAnalysis: {
    sort: 11,
    name: $i18n.get({
      id: 'aopf.src.menuConfig.MiniProgramOperationAnalysis',
      dm: '小程序运营分析',
    }),
    // path: '/miniappOperationAnalysis',
    icon: <span className="utopia_iconfont">&#xe88d;</span>,
    children: [
      {
        name: $i18n.get({ id: 'aopf.src.menuConfig.StatisticalKanban', dm: '统计看板' }),
        path: '/ly_analyse/platform/apps/board',
        icon: <span className="utopia_iconfont">&#xe897;</span>,
      },
      {
        name: $i18n.get({ id: 'aopf.src.menuConfig.BehavioralInsight', dm: '行为洞察' }),
        path: '/ly_analyse/platform/apps/analyse',
        icon: <span className="utopia_iconfont">&#xe7fe;</span>,
      },
    ],
  },
  miniappOperationAnalysisAdmin: {
    sort: 10,
    name: $i18n.get({
      id: 'aopf.src.menuConfig.MiniProgramOperationAnalysis',
      dm: '小程序运营分析',
    }),
    // path: '/miniappOperationAnalysisAdmin',
    icon: <span className="utopia_iconfont">&#xe88d;</span>,
    children: [
      {
        name: $i18n.get({ id: 'aopf.src.menuConfig.StatisticalKanban', dm: '统计看板' }),
        path: '/ly_analyse/platform/apps/board',
        icon: <span className="utopia_iconfont">&#xe897;</span>,
      },
      {
        name: $i18n.get({ id: 'aopf.src.menuConfig.BehavioralInsight', dm: '行为洞察' }),
        path: '/ly_analyse/platform/apps/analyse',
        icon: <span className="utopia_iconfont">&#xe7fe;</span>,
      },
      {
        name: $i18n.get({ id: 'aopf.src.menuConfig.BuriedPointManagement', dm: '埋点管理' }),
        path: '/ly_track/track/manage/event',
        icon: <span className="utopia_iconfont">&#xe9ae;</span>,
      },
      {
        name: $i18n.get({ id: 'aopf.src.menuConfig.BuriedPointVerification', dm: '埋点验证' }),
        path: '/ly_track/track/check/begin',
        icon: <span className="utopia_iconfont">&#xea00;</span>,
      },
    ],
  },
  smartStudio: {
    name: $i18n.get({ id: 'aopf.src.menuConfig.IntelligentPaintingPlatform', dm: '智绘平台' }),
    path: '/smartStudio',
    icon: <span className="utopia_iconfont">&#xea02;</span>,
    sort: 9,
  },
};

/**
  权限改造：
  
  1.管理员[ADMIN] 
  菜单: 管理员
  邀请: 管理员+开发者

  2.开发者[DEVELOPER] 
  菜单: 开发者
  不可邀请

  3.AIGC设计者[AIGC_DESIGNER] 
  菜单: 智绘平台+成员列表
  邀请: AIGC设计者

  4.运营管理员[OPERATOR]
  菜单: 运营管理员

  4.管理员+开发者[ADMIN, DEVELOPER] 
  菜单: 管理员
  列表: 管理员
  邀请: 管理员+开发者

  5.管理员+AIGC设计者[ADMIN, AIGC_DESIGNER] 
  菜单: 管理员+智绘平台
  列表: 管理员+AIGC设计者
  邀请: 管理员+开发者+AIGC设计者

  6.开发者+AIGC设计者[DEVELOPER, AIGC_DESIGNER] 
  菜单: 开发者+智绘平台
  列表: 开发者+AIGC设计者
  邀请: AIGC设计者

  7.管理员+开发者+AIGC设计者[ADMIN, DEVELOPER, AIGC_DESIGNER]
  菜单: 管理员+智绘平台
  列表: 管理员+AIGC设计者
  邀请: 管理员+开发者+AIGC设计者
 */
const roleMenuList = {
  CONSUMER: {
    ADMIN: [
      menus.overview,
      menus.miniapp,
      menus.members,
      menus.approvals,
      menus.channelSetting,
      menus.channels,
      menus.invitation,
    ],
    DEVELOPER: isPublicDeploy()
      ? [menus.overview, menus.miniapp, menus.members, menus.approvals, menus.channels]
      : [menus.overview, menus.miniapp, menus.members, menus.approvals],
    AIGC_DESIGNER: [menus.members, menus.smartStudio],
  },
  PARTNER: {
    ADMIN: [menus.overview, menus.miniapp, menus.members, menus.approvals],
    DEVELOPER: [menus.overview, menus.miniapp, menus.members, menus.approvals],
    AIGC_DESIGNER: [menus.members, menus.smartStudio],
  },
};

export const specialActivePathMenuMap  = {
   // 统计看板
  '/ly_analyse/platform/apps/board': /^\/ly_analyse\/platform\/([a-z0-9]+)\/(analyse|analysis_insight\/board)/,
   // 行为洞察
  '/ly_analyse/platform/apps/analyse': /^\/ly_analyse\/platform\/([a-z0-9]+)\/(analyse|analysis_insight\/(segment|funnel|retention|distributionAnalysis|intervalAnalysis|attribution|session|path))/,
  // 埋点管理
  '/ly_track/track/manage/event': /^\/ly_track\/track\/manage\/event/,
  // 埋点验证
  '/ly_track/track/check/begin': /^\/ly_track\/track\/check/

}

export function getMenuConfig(state: SpaceAuthority, userState: UserInfo): MenuDataItem[] {
  if (!state.isUpdate || !userState.isUpdate) {
    return [];
  }

  const _roleList = state.roleList || [];

  let defaultMenu: any = [];
  _roleList.forEach((v) => {
    defaultMenu = defaultMenu.concat(roleMenuList[state.type!!]?.[v] || []);
  });

  if (_roleList.includes('ADMIN')) {
    // 管理员，拥有「小程序运营分析」菜单中的所有功能的查看编辑权限
    defaultMenu = defaultMenu.concat(menus.miniappOperationAnalysisAdmin);
  }

  if (_roleList.includes('OPERATOR') && !_roleList.includes('ADMIN')) {
    // OPERATOR
    defaultMenu = defaultMenu.concat(menus.miniappOperationAnalysis);
  }

  defaultMenu = uniqBy(defaultMenu, 'path').sort((a, b) => a.sort - b.sort);

  if (userState?.isDuibaUser) {
    defaultMenu.push(menus.duiba);
  }

  return defaultMenu;
}

